import { useCallback, useMemo } from 'react'

import { useControllableState } from '@radix-ui/react-use-controllable-state'

import { truncateText } from 'ui/helpers/utilities'

import { ItemSelectorItem } from './types'

type UseItemSelectorStateOptions = {
    items: ItemSelectorItem[]
    value?: string[]
    onChange?: (value: string[]) => void
    maxLength?: number
    maxSelectedItems?: number
}

export function useItemSelectorState(options: UseItemSelectorStateOptions) {
    const {
        items,
        value: providedValue,
        onChange: providedOnChange,
        maxLength,
        maxSelectedItems,
    } = options

    const itemOptions: ItemSelectorItem[] = useMemo(() => {
        return items.map((item) => {
            const label = maxLength ? truncateText(item.label, maxLength) : item.label

            return {
                ...item,
                label,
            }
        }, [])
    }, [items, maxLength])

    const filteredProvidedValue = useMemo(() => {
        if (!providedValue) return []
        const validValues = new Set(itemOptions.map((item) => item.value))
        return providedValue.filter((value) => validValues.has(value))
    }, [providedValue, itemOptions])

    const [value = [], onChange] = useControllableState({
        prop: filteredProvidedValue,
        onChange: providedOnChange,
        defaultProp: [],
    })

    const onCheckedChange = useCallback(
        (itemValue: string, checked: boolean) => {
            if (checked) {
                onChange([...value, itemValue])
            } else {
                onChange(value.filter((iv) => iv !== itemValue))
            }
        },
        [onChange, value]
    )

    const valueSet = useMemo(() => new Set(value), [value])
    const isSelected = useCallback((itemValue: string) => valueSet.has(itemValue), [valueSet])

    const selectedOptions = useMemo(() => {
        const itemOptionsMap = new Map(itemOptions.map((item) => [item.value, item]))

        return value.reduce((acc, value) => {
            const item = itemOptionsMap.get(value)
            if (item) {
                acc.push(item)
            }

            return acc
        }, [] as ItemSelectorItem[])
    }, [itemOptions, value])

    let showAddButton = maxSelectedItems ? selectedOptions.length < maxSelectedItems : true
    if (itemOptions.length < 1) {
        showAddButton = false
    }

    const shouldShow = selectedOptions.length > 0 || showAddButton

    return useMemo(
        () => ({
            itemOptions,
            onCheckedChange,
            isSelected,
            selectedOptions,
            showAddButton,
            shouldShow,
        }),
        [itemOptions, onCheckedChange, isSelected, selectedOptions, showAddButton, shouldShow]
    )
}
